/**
 * @file     BreakEvenChartCard.tsx
 * @purpose  Year-by-year SVG chart comparing buyer vs renter net worth. Smooth Catmull-Rom curves, gradient area fills under each line, an after-break-even "buying wins" green tint, an animated stroke-dasharray draw on first paint, hover guide with dual dots and a tooltip card, glowing break-even marker with a floating badge. Tokens-only colors and prefers-reduced-motion safe.
 */
'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { RentVsBuyResult } from './rentVsBuyMath';

type Props = {
  result: RentVsBuyResult;
  loading?: boolean;
};

const VIEW_W = 600;
const VIEW_H = 300;
const PAD_LEFT = 64;
const PAD_RIGHT = 24;
const PAD_TOP = 28;
const PAD_BOTTOM = 36;
const INNER_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const INNER_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
const GRIDLINES = 4;

const fmtCADCompact = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${Math.round(abs)}`;
};

const fmtCADFull = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0,
  );

function niceMax(raw: number): number {
  if (raw <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function niceMin(raw: number): number {
  if (raw >= 0) return 0;
  const abs = Math.abs(raw);
  const magnitude = Math.pow(10, Math.floor(Math.log10(abs)));
  const normalized = abs / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return -nice * magnitude;
}

/**
 * Build a Catmull-Rom cubic bezier path through the supplied points so the
 * line reads as a smooth curve rather than connected straight segments.
 * Tension 0.5 produces the relaxed, financial-dashboard look.
 */
function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  const tension = 0.5;
  let d = `M${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const c1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const c2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const c2y = p2.y - ((p3.y - p1.y) / 6) * tension;
    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
}

/** Append closing segment so we can fill the area under the line. */
function closePath(linePath: string, baselineY: number, firstX: number, lastX: number): string {
  return `${linePath} L${lastX.toFixed(2)},${baselineY.toFixed(2)} L${firstX.toFixed(2)},${baselineY.toFixed(2)} Z`;
}

export const BreakEvenChartCard = ({ result, loading = false }: Props) => {
  const { theme } = useAppTheme();
  const uid = useId().replace(/:/g, '');
  const series = result.series;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // First-paint stroke-dasharray animation. Set to true after mount + a tick
  // so the keyframe runs on the very first render.
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDrawn(true);
      return;
    }
    const t = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const buyerColorRaw = theme.rawSemanticColors.interactivePrimary;
  const renterColorRaw = theme.rawSemanticColors.statusInfo;
  const successRaw = theme.rawSemanticColors.statusSuccess;

  const computed = useMemo(() => {
    if (series.length === 0) return null;
    const allVals = series.flatMap((p) => [p.buyerNetWorth, p.renterNetWorth]);
    const rawMax = Math.max(...allVals, 0);
    const rawMin = Math.min(...allVals, 0);
    const maxVal = niceMax(rawMax * 1.05);
    const minVal = niceMin(rawMin * 1.05);
    const range = maxVal - minVal || 1;

    const toX = (i: number) => PAD_LEFT + (i / Math.max(series.length - 1, 1)) * INNER_W;
    const toY = (v: number) => PAD_TOP + INNER_H - ((v - minVal) / range) * INNER_H;

    const buyerPts = series.map((p, i) => ({ x: toX(i), y: toY(p.buyerNetWorth) }));
    const renterPts = series.map((p, i) => ({ x: toX(i), y: toY(p.renterNetWorth) }));
    const buyerLinePath = smoothPath(buyerPts);
    const renterLinePath = smoothPath(renterPts);
    const baselineY = toY(0);
    const firstX = toX(0);
    const lastX = toX(series.length - 1);
    const buyerAreaPath = closePath(buyerLinePath, baselineY, firstX, lastX);
    const renterAreaPath = closePath(renterLinePath, baselineY, firstX, lastX);

    const gridlines = Array.from({ length: GRIDLINES + 1 }, (_, k) => {
      const frac = k / GRIDLINES;
      const val = minVal + frac * range;
      return { y: toY(val), label: fmtCADCompact(val) };
    });

    const totalYears = series[series.length - 1].year;
    const stride = totalYears <= 10 ? 1 : 5;
    const xTicks = series
      .filter((p) => p.year === 1 || p.year === totalYears || p.year % stride === 0)
      .filter((p, idx, arr) => arr.findIndex((q) => q.year === p.year) === idx)
      .map((p) => ({ year: p.year, x: toX(series.findIndex((s) => s.year === p.year)) }));

    let crossX: number | null = null;
    let crossYBuyer: number | null = null;
    if (result.breakEvenYear !== null) {
      const idx = series.findIndex((p) => p.year === result.breakEvenYear);
      if (idx !== -1) {
        crossX = toX(idx);
        crossYBuyer = toY(series[idx].buyerNetWorth);
      }
    }

    return {
      buyerLinePath,
      renterLinePath,
      buyerAreaPath,
      renterAreaPath,
      gridlines,
      xTicks,
      crossX,
      crossYBuyer,
      baselineY,
      totalYears,
      xPositions: buyerPts.map((p) => p.x),
      buyerYs: buyerPts.map((p) => p.y),
      renterYs: renterPts.map((p) => p.y),
      lastX,
    };
  }, [series, result.breakEvenYear]);

  const fastMs = theme.motion.durations.fast;
  const slowMs = theme.motion.durations.slow;
  const easing = theme.motion.easing.standard;

  const DRAW_LEN = 4000;
  const drawClass = `${uid}-draw`;
  const lineClass = `${uid}-line`;
  const cssRules = `
    .${drawClass} {
      stroke-dasharray: ${DRAW_LEN};
      stroke-dashoffset: ${DRAW_LEN};
      transition: stroke-dashoffset ${slowMs}ms ${easing};
    }
    .${drawClass}.${uid}-on { stroke-dashoffset: 0; }
    .${lineClass} { transition: stroke-width ${fastMs}ms ${easing}; }
    @media (prefers-reduced-motion: reduce) {
      .${drawClass} { transition: none; stroke-dashoffset: 0; }
      .${lineClass} { transition: none; }
    }
  `;

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!computed || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = VIEW_W / rect.width;
    const xInView = (e.clientX - rect.left) * ratio;
    let bestI = 0;
    let bestD = Infinity;
    for (let i = 0; i < computed.xPositions.length; i += 1) {
      const d = Math.abs(computed.xPositions[i] - xInView);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    }
    setHoverIdx(bestI);
  };

  const onPointerLeave = () => setHoverIdx(null);

  if (loading || !computed) {
    return (
      <Box
        sx={{
          bgcolor: theme.semanticColors.surfaceCard,
          borderRadius: `${theme.layout.radius.md}px`,
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="rounded" height={280} sx={{ mt: 1.5, borderRadius: `${theme.layout.radius.sm}px` }} />
      </Box>
    );
  }

  const ariaLabel =
    result.breakEvenYear !== null
      ? `Net worth chart. Buying overtakes renting at year ${result.breakEvenYear}.`
      : `Net worth chart. Renting wins through year ${computed.totalYears}.`;

  const hoverPoint = hoverIdx !== null && series[hoverIdx] ? series[hoverIdx] : null;
  const hoverX = hoverIdx !== null ? computed.xPositions[hoverIdx] : null;
  const tooltipOnLeft = hoverX !== null && hoverX > VIEW_W * 0.6;

  const buyerGradId = `${uid}-buyer-grad`;
  const renterGradId = `${uid}-renter-grad`;
  const winZoneClipId = `${uid}-win-zone`;
  const winZoneGradId = `${uid}-win-grad`;

  return (
    <Box
      sx={{
        bgcolor: theme.semanticColors.surfaceCard,
        borderRadius: `${theme.layout.radius.md}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        p: { xs: 2, sm: 2.5 },
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: cssRules }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamilyHeading,
              fontSize: theme.typography.h4,
              fontWeight: 600,
              color: theme.semanticColors.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            Net worth over time
          </Typography>
          <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, mt: 0.25 }}>
            Your equity if you buy versus your portfolio if you keep renting and invest the difference.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexShrink: 0 }}>
          <LegendDot color={buyerColorRaw} label="Buying" />
          <LegendDot color={renterColorRaw} label="Renting" />
        </Box>
      </Box>

      <Box sx={{ width: '100%', minWidth: 0, position: 'relative', mt: 1.5 }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          style={{ display: 'block', overflow: 'hidden', maxWidth: '100%' }}
          role="img"
          aria-label={ariaLabel}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
        >
          <defs>
            <linearGradient id={buyerGradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={buyerColorRaw} stopOpacity="0.30" />
              <stop offset="100%" stopColor={buyerColorRaw} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={renterGradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={renterColorRaw} stopOpacity="0.22" />
              <stop offset="100%" stopColor={renterColorRaw} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={winZoneGradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={successRaw} stopOpacity="0.10" />
              <stop offset="100%" stopColor={successRaw} stopOpacity="0" />
            </linearGradient>
            {computed.crossX !== null && (
              <clipPath id={winZoneClipId}>
                <rect
                  x={computed.crossX}
                  y={PAD_TOP}
                  width={Math.max(0, computed.lastX - computed.crossX)}
                  height={INNER_H}
                />
              </clipPath>
            )}
          </defs>

          {computed.gridlines.map((gl, k) => (
            <g key={k}>
              <line
                x1={PAD_LEFT}
                y1={gl.y}
                x2={VIEW_W - PAD_RIGHT}
                y2={gl.y}
                stroke={alpha(theme.rawSemanticColors.borderSubtle, 0.6)}
                strokeWidth="1"
                strokeDasharray={k === 0 ? '0' : '2 4'}
              />
              <text
                x={PAD_LEFT - 8}
                y={gl.y}
                textAnchor="end"
                dominantBaseline="middle"
                style={{ fontSize: 10, fill: theme.rawSemanticColors.textMuted, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }}
              >
                {gl.label}
              </text>
            </g>
          ))}

          <line
            x1={PAD_LEFT}
            y1={computed.baselineY}
            x2={VIEW_W - PAD_RIGHT}
            y2={computed.baselineY}
            stroke={alpha(theme.rawSemanticColors.textMuted, 0.5)}
            strokeWidth="1"
            strokeDasharray="3 3"
          />

          {computed.crossX !== null && (
            <rect
              x={computed.crossX}
              y={PAD_TOP}
              width={Math.max(0, computed.lastX - computed.crossX)}
              height={INNER_H}
              fill={`url(#${winZoneGradId})`}
              clipPath={`url(#${winZoneClipId})`}
            />
          )}

          <path d={computed.renterAreaPath} fill={`url(#${renterGradId})`} />
          <path d={computed.buyerAreaPath} fill={`url(#${buyerGradId})`} />

          <path
            className={`${lineClass} ${drawClass} ${drawn ? `${uid}-on` : ''}`}
            d={computed.renterLinePath}
            fill="none"
            stroke={renterColorRaw}
            strokeWidth={hoverIdx !== null ? 2.75 : 2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.95}
          />

          <path
            className={`${lineClass} ${drawClass} ${drawn ? `${uid}-on` : ''}`}
            d={computed.buyerLinePath}
            fill="none"
            stroke={buyerColorRaw}
            strokeWidth={hoverIdx !== null ? 3 : 2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ transitionDelay: '120ms' }}
          />

          {computed.crossX !== null && computed.crossYBuyer !== null && (
            <g>
              <line
                x1={computed.crossX}
                y1={PAD_TOP}
                x2={computed.crossX}
                y2={PAD_TOP + INNER_H}
                stroke={alpha(successRaw, 0.45)}
                strokeWidth="1.25"
                strokeDasharray="4 3"
              />
              <circle cx={computed.crossX} cy={computed.crossYBuyer} r="14" fill={alpha(successRaw, 0.10)} />
              <circle cx={computed.crossX} cy={computed.crossYBuyer} r="9" fill={alpha(successRaw, 0.20)} />
              <circle cx={computed.crossX} cy={computed.crossYBuyer} r="5" fill={successRaw} stroke={theme.rawSemanticColors.surfaceCard} strokeWidth="2" />
            </g>
          )}

          {hoverIdx !== null && hoverX !== null && hoverPoint && (
            <g>
              <line
                x1={hoverX}
                y1={PAD_TOP}
                x2={hoverX}
                y2={PAD_TOP + INNER_H}
                stroke={alpha(theme.rawSemanticColors.textMuted, 0.5)}
                strokeWidth="1"
              />
              <circle cx={hoverX} cy={computed.buyerYs[hoverIdx]} r="4" fill={buyerColorRaw} stroke={theme.rawSemanticColors.surfaceCard} strokeWidth="2" />
              <circle cx={hoverX} cy={computed.renterYs[hoverIdx]} r="4" fill={renterColorRaw} stroke={theme.rawSemanticColors.surfaceCard} strokeWidth="2" />
            </g>
          )}

          {computed.xTicks.map((tick) => (
            <g key={tick.year}>
              <line
                x1={tick.x}
                y1={PAD_TOP + INNER_H}
                x2={tick.x}
                y2={PAD_TOP + INNER_H + 4}
                stroke={alpha(theme.rawSemanticColors.textMuted, 0.5)}
                strokeWidth="1"
              />
              <text
                x={tick.x}
                y={PAD_TOP + INNER_H + 18}
                textAnchor="middle"
                style={{ fontSize: 10, fill: theme.rawSemanticColors.textMuted, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }}
              >
                {tick.year === 1 || tick.year === computed.totalYears ? `Yr ${tick.year}` : tick.year}
              </text>
            </g>
          ))}

          <line x1={PAD_LEFT} y1={PAD_TOP + INNER_H} x2={VIEW_W - PAD_RIGHT} y2={PAD_TOP + INNER_H} stroke={alpha(theme.rawSemanticColors.textMuted, 0.4)} strokeWidth="1" />
          <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={PAD_TOP + INNER_H} stroke={alpha(theme.rawSemanticColors.textMuted, 0.4)} strokeWidth="1" />
        </svg>

        {/* Floating break-even badge above the marker */}
        {computed.crossX !== null && computed.crossYBuyer !== null && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: `${(computed.crossX / VIEW_W) * 100}%`,
              top: `${((computed.crossYBuyer - 32) / VIEW_H) * 100}%`,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              bgcolor: theme.semanticColors.surfaceCard,
              border: `1px solid ${alpha(successRaw, 0.4)}`,
              borderRadius: `${theme.layout.radius.md}px`,
              boxShadow: `0 4px 12px ${alpha(successRaw, 0.18)}`,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: successRaw }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: theme.semanticColors.statusSuccess,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Break-even, year {result.breakEvenYear}
            </Typography>
          </Box>
        )}

        {hoverPoint && hoverX !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: tooltipOnLeft ? 'auto' : `${(hoverX / VIEW_W) * 100}%`,
              right: tooltipOnLeft ? `${((VIEW_W - hoverX) / VIEW_W) * 100}%` : 'auto',
              transform: tooltipOnLeft ? 'translateX(-12px)' : 'translateX(12px)',
              pointerEvents: 'none',
              bgcolor: theme.semanticColors.surfaceElevated,
              border: `1px solid ${theme.semanticColors.borderSubtle}`,
              borderRadius: `${theme.layout.radius.sm}px`,
              boxShadow: `0 8px 24px ${alpha(theme.rawSemanticColors.textPrimary, 0.10)}`,
              px: 1.25,
              py: 1,
              minWidth: 184,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Typography sx={{ fontSize: 11, color: theme.semanticColors.textMuted, mb: 0.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Year {hoverPoint.year}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: buyerColorRaw }} />
                <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary }}>Buying</Typography>
              </Box>
              <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textPrimary, fontWeight: 600 }}>
                {fmtCADFull(hoverPoint.buyerNetWorth)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: renterColorRaw }} />
                <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary }}>Renting</Typography>
              </Box>
              <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textPrimary, fontWeight: 600 }}>
                {fmtCADFull(hoverPoint.renterNetWorth)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 0.75, pt: 0.75, borderTop: `1px solid ${theme.semanticColors.borderSubtle}` }}>
              <Typography sx={{ fontSize: 11, color: theme.semanticColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                Difference
              </Typography>
              <Typography
                sx={{
                  fontSize: theme.typography.bodySm,
                  color:
                    hoverPoint.buyerNetWorth >= hoverPoint.renterNetWorth
                      ? theme.semanticColors.statusSuccess
                      : theme.semanticColors.textSecondary,
                  fontWeight: 600,
                }}
              >
                {hoverPoint.buyerNetWorth >= hoverPoint.renterNetWorth ? '+' : ''}
                {fmtCADFull(hoverPoint.buyerNetWorth - hoverPoint.renterNetWorth)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => {
  const { theme } = useAppTheme();
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 18, height: 3, borderRadius: '2px', bgcolor: color }} />
      <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
};
